import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * Drag a bottom sheet down to dismiss it.
 *
 * CSS selector: none of its own. It hands back an inline transform and the
 * `dsa-motion-panel--drag` / `--settle` classes that `patterns/motion.css`
 * answers.
 *
 * Doctrine:
 *
 * - THE HANDLE IS THE GRABBER AND THE HEADER, never the body. The body is the
 *   sheet's only scrolling region, and a gesture that owns both axes there has
 *   to guess on every touch whether a downward drag means "scroll the list" or
 *   "dismiss the sheet". Restricting the handle removes the guess instead of
 *   tuning it: a filter sheet's list scrolls exactly as it always did.
 * - A DRAG NEVER STARTS ON A CONTROL. Pressing the close button, a chip or a
 *   link inside the header must not also pick the sheet up, or a mis-swiped
 *   press dismisses the sheet AND fires the control.
 * - DOWN ONLY. `Math.max(0, ...)` clamps the travel, so pulling up cannot lift
 *   the sheet off the bottom edge and leave a strip of page under it.
 * - TWO WAYS TO CLOSE, and both are needed. Distance alone ignores a fast flick
 *   that only travels 60px, which is the most natural way to throw a sheet
 *   away; velocity alone closes on a twitch. Either gate closes.
 * - RELEASING KEEPS THE OFFSET. The exit keyframe in `patterns/motion.css` has
 *   no `from`, so it starts from whatever the panel's underlying transform is:
 *   leave the drag offset in place and the sheet continues down from where the
 *   thumb let go. Clearing it first would snap the sheet back to rest and only
 *   then play the exit, which reads as the gesture being undone.
 *
 * Pointer Events, not touch events, and capture is the reason. A grabber is
 * 40x4px; a thumb leaves it within the first few pixels of any real drag, and
 * without `setPointerCapture` the move events go to whatever is under the
 * finger instead. Capture also gets mouse and pen for free, which is what makes
 * the gesture testable at all.
 *
 * @param {{ panelRef: React.RefObject<HTMLElement>, onClose?: (reason: string) => void, enabled?: boolean }} options
 */

/* Fraction of the sheet's own height that counts as "gone". A quarter is
   forgiving on a tall sheet (a 700px filter sheet needs 175px, a real pull)
   and still reachable on a short one, which a fixed pixel threshold is not:
   120px is a third of a 360px sheet and a sixth of a 700px one. */
const CLOSE_FRACTION = 0.25;

/* px/ms downward at release. 0.5 is ~a third of the way down a 400px sheet in
   the ~250ms a flick lasts -- deliberate, but nothing a slow considered pull
   reaches. */
const FLING_VELOCITY = 0.5;

/* The spring back. Shorter than the 280ms exit because nothing is leaving:
   the sheet is returning to a position it never really left, and a return that
   takes as long as a dismissal reads as hesitation. */
const SETTLE_MS = 200;

/* A press that lands on any of these is the control's, not the sheet's.
   `[role="button"]` catches the ones built out of divs; `label` catches the
   tap target of a checkbox row, which is most of a filter sheet's header. */
const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"], [role="slider"]';

export function useSheetDrag({ panelRef, onClose, enabled = true }) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);
  const gesture = useRef(null);
  const settleTimer = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => () => clearTimeout(settleTimer.current), []);

  /* Reset on the way back IN, not on the way out. `enabled` is false for the
     length of the exit precisely so the released offset survives it and the
     sheet leaves from where the thumb dropped it; clearing there would undo
     that. By the time it goes true again the sheet is arriving fresh and any
     leftover offset would be a sheet that opens already half-dismissed. */
  useEffect(() => {
    if (!enabled) return;
    setOffset(0);
    setSettling(false);
  }, [enabled]);

  const panelHeight = useCallback(
    () => (panelRef.current && panelRef.current.offsetHeight) || 0,
    [panelRef],
  );

  const onPointerDown = useCallback(
    (event) => {
      if (!enabled || !onClose) return;
      if (event.target.closest && event.target.closest(INTERACTIVE)) return;

      gesture.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastAt: Date.now(),
        velocity: 0,
      };

      clearTimeout(settleTimer.current);
      setSettling(false);
      setDragging(true);
      setOffset(0);

      const node = event.currentTarget;
      if (node.setPointerCapture) node.setPointerCapture(event.pointerId);
    },
    [enabled, onClose],
  );

  const onPointerMove = useCallback((event) => {
    const active = gesture.current;
    if (!active || event.pointerId !== active.pointerId) return;

    const now = Date.now();
    /* Velocity from the LAST SEGMENT, not the whole gesture. A thumb that
       hesitates halfway and then flicks has a low average and a high final
       speed, and the flick is the intent. `Math.max(1, ...)` keeps two events
       inside the same millisecond from dividing by zero. */
    active.velocity = (event.clientY - active.lastY) / Math.max(1, now - active.lastAt);
    active.lastY = event.clientY;
    active.lastAt = now;

    setOffset(Math.max(0, event.clientY - active.startY));
  }, []);

  const onPointerUp = useCallback(
    (event) => {
      const active = gesture.current;
      if (!active || event.pointerId !== active.pointerId) return;
      gesture.current = null;

      const node = event.currentTarget;
      if (node.releasePointerCapture) node.releasePointerCapture(active.pointerId);

      const travelled = Math.max(0, active.lastY - active.startY);
      const height = panelHeight();
      const pastThreshold = height > 0 && travelled > height * CLOSE_FRACTION;
      const flung = active.velocity > FLING_VELOCITY && travelled > 0;

      setDragging(false);

      if (pastThreshold || flung) {
        /* Offset deliberately left where it is -- see the doctrine above. */
        if (onClose) onClose('drag');
        return;
      }

      setOffset(0);
      setSettling(true);
      settleTimer.current = setTimeout(() => setSettling(false), reduced ? 0 : SETTLE_MS);
    },
    [onClose, panelHeight, reduced],
  );

  /* Cancel is a real case, not a formality: an incoming call, a system gesture
     or the browser taking the pointer for its own scroll all fire this and
     never fire `pointerup`. Without it the sheet stays frozen wherever the
     thumb was when the OS took over. */
  const onPointerCancel = useCallback(
    (event) => {
      const active = gesture.current;
      if (!active || event.pointerId !== active.pointerId) return;
      gesture.current = null;
      setDragging(false);
      setOffset(0);
      setSettling(true);
      settleTimer.current = setTimeout(() => setSettling(false), reduced ? 0 : SETTLE_MS);
    },
    [reduced],
  );

  const active = enabled && Boolean(onClose);

  /* One object, spread onto every handle. `touch-action: none` is a CSS job
     (`patterns/motion.css`), which is why the class travels with the handlers:
     without it the browser claims the vertical gesture for scrolling or
     pull-to-refresh before a single `pointermove` is delivered. */
  const handleProps = active
    ? {
        className: 'dsa-sheet-handle',
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
      }
    : null;

  const height = panelHeight();

  return {
    dragging,
    settling,
    handleProps,
    /* Applied whenever the panel is off its resting position OR on its way
       back to it -- the settle needs a `translateY(0px)` to transition TO, and
       an idle sheet needs no inline transform at all. */
    panelStyle:
      dragging || settling || offset > 0 ? { transform: `translateY(${offset}px)` } : undefined,
    /* The scrim thins as the sheet leaves, so a half-dismissed sheet shows
       half a page behind it and the gesture reads as reversible. Only while
       the thumb is down: the settle and the exit are the animations' job. */
    scrimStyle:
      dragging && height > 0
        ? { opacity: String(Math.max(0, Math.min(1, 1 - offset / height))) }
        : undefined,
  };
}

export default useSheetDrag;
